/**
 * A stacked icon-over-label target, for a region whose whole job is to be chosen from.
 *
 * The block rail and the image block's empty state had each built this independently, and arrived
 * at the same markup without knowing about each other:
 *
 *   rail:         flex w-full flex-col items-center gap-1.5 rounded-lg border px-2 py-3 + icon h-5 w-5 + text-xs
 *   empty state:  flex     flex-col items-center gap-1.5 rounded-lg border px-4 py-3 + icon h-5 w-5 + text-xs
 *
 * Two people reaching the same form for different actions — one adds a block, the other opens a
 * file picker — is the argument for the component. What they share is not what the click does; it
 * is the situation. So the line between this and `Button` is NOT act-versus-pick, which was the
 * obvious guess and the wrong one. It is:
 *
 *   Is the control sitting in a row alongside other controls, or is it the CONTENT of its region?
 *
 * A row wants a `Button`. A region that exists to be chosen from wants one of these, whatever the
 * click goes on to do. Flattening an empty state into a row of labelled buttons would make it read
 * as a toolbar.
 */
import { forwardRef } from 'react';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

const BASE =
  'flex flex-col items-center justify-center gap-1.5 rounded-lg border text-ws-text '
  + 'transition-colors duration-300 focus-visible:[outline:2px_auto] '
  + '[&:focus:not(:focus-visible)]:outline-none [&_svg]:h-5 [&_svg]:w-5 '
  + 'disabled:cursor-not-allowed';

/**
 * `roomy` is the empty-state shape (its own row, so it can afford side padding); `rail` fills a
 * narrow column. The difference between them is the horizontal padding and nothing else, which is
 * why they are one component rather than two.
 */
const WIDTH = { roomy: 'px-4 py-3', rail: 'w-full px-2 py-3' } as const;

/**
 * Which ground the tile sits on, because a tile has to contrast with what is behind it.
 *
 * The rail is on `ws-sidebar` and the empty state is inside a card, and the two differ in dark mode
 * (`#0E2243` against `#163253`) even though both are white in light. A `className` override would
 * not do: two background utilities set the same property, so which one wins is stylesheet order
 * rather than specificity — the trap that put the search placeholder under its own icon.
 */
const SURFACE = { btn: 'bg-ws-btn', elevated: 'bg-ws-elevated' } as const;

export interface ChoiceTileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'> {
  icon: ReactNode;
  label: string;
  /** `rail` for a narrow column of them, `roomy` for a row on an open surface. */
  fit?: keyof typeof WIDTH;
  /** The ground behind it — see `SURFACE`. */
  surface?: keyof typeof SURFACE;
  /**
   * Draw it as unavailable but still explain itself — the block rail greys a block type that the
   * task cannot take yet and puts the reason beside it, rather than hiding the tile.
   */
  disabled?: boolean;
}

export const ChoiceTile = forwardRef<HTMLButtonElement, ChoiceTileProps>(function ChoiceTile(
  { icon, label, fit = 'roomy', surface = 'btn', disabled, className = '', ...props },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type='button'
      disabled={disabled}
      className={`${BASE} ${WIDTH[fit]} ${
        disabled
          ? 'border-ws-border bg-ws-disabled-bg text-ws-text-disabled'
          : `border-ws-border ${SURFACE[surface]} hover:bg-ws-card-hover hover:text-ws-btn-active-fg`
      } ${className}`}
    >
      {icon}
      {/* Full text colour, not muted. Three muted labels alone on a card have nothing to be muted
          RELATIVE to, so the whole empty state reads as disabled — which is how the image block's
          own version looked. */}
      <span className='text-center text-xs font-medium leading-tight'>{label}</span>
    </button>
  );
});
